import React, { useCallback, useEffect, useState } from 'react';
import {
    AlertTriangle,
    CheckCircle2,
    Database,
    DollarSign,
    Loader2,
    Plus,
    ReceiptText,
    RefreshCw,
    Sparkles,
} from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import AccessRestricted from './AccessRestricted';
import { MetricCard } from './common/MetricCard';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from './ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from './ui/table';
import { aiUsageApi } from '../utils/aiUsage-api';
import { AiModelRate, AiUsageCall, AiUsageSummary } from '../types/aiUsage.types';
import { cn } from './ui/utils';

type Period = 'today' | '7d' | '30d' | 'month' | 'all';

const PERIODS: { value: Period; label: string }[] = [
    { value: 'today', label: 'Today' },
    { value: '7d', label: 'Last 7 days' },
    { value: '30d', label: 'Last 30 days' },
    { value: 'month', label: 'This month' },
    { value: 'all', label: 'All time' },
];

const dateKey = (date: Date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
};

const periodRange = (period: Period): { from: string | null; to: string | null } => {
    if (period === 'all') return { from: null, to: null };

    const today = new Date();
    const start = new Date(today.getFullYear(), today.getMonth(), today.getDate());
    if (period === '7d') start.setDate(start.getDate() - 6);
    if (period === '30d') start.setDate(start.getDate() - 29);
    if (period === 'month') start.setDate(1);

    return { from: dateKey(start), to: dateKey(today) };
};

const num = (value: number | null | undefined) => Number(value ?? 0).toLocaleString('en-AU');
const money = (value: number | null | undefined) => (
    value === null || value === undefined ? '—' : `$${Number(value).toFixed(2)}`
);
const rateMoney = (value: number | null | undefined) => (
    value === null || value === undefined
        ? '—'
        : Number(value).toLocaleString('en-AU', {
            minimumFractionDigits: 2,
            maximumFractionDigits: 4,
        })
);
const parseRateField = (value: string): number | null => {
    if (value.trim() === '') return null;
    const parsed = Number(value);
    return Number.isFinite(parsed) && parsed >= 0 ? parsed : null;
};
const sydneyTimestamp = (value: string) => {
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return '—';
    return date.toLocaleString('en-AU', {
        timeZone: 'Australia/Sydney',
        day: '2-digit',
        month: 'short',
        hour: '2-digit',
        minute: '2-digit',
    });
};

const orderTypeLabel = (type: string | null) => {
    const labels: Record<string, string> = {
        delivery: 'Deliveries',
        return: 'Returns',
        import: 'Imports',
        transfer: 'Transfers',
    };
    if (!type) return 'Unlinked calls';
    return labels[type] || type.charAt(0).toUpperCase() + type.slice(1);
};

const statusPillClass = (status: string) => {
    if (status === 'success') return 'bg-emerald-50 text-emerald-700 border-emerald-200';
    if (status === 'refusal' || status === 'invalid_output') {
        return 'bg-amber-50 text-amber-700 border-amber-200';
    }
    if (status === 'rate_limited' || status === 'timeout' || status === 'error') {
        return 'bg-red-50 text-red-700 border-red-200';
    }
    return 'bg-muted text-muted-foreground border-border';
};

const totalTokens = (tokens: {
    inputTokens: number;
    cachedReadTokens: number;
    cacheWriteTokens: number;
    outputTokens: number;
}) => tokens.inputTokens + tokens.cachedReadTokens + tokens.cacheWriteTokens + tokens.outputTokens;

const emptyRateForm = () => ({
    model: '',
    effectiveFrom: dateKey(new Date()),
    inputPerMillion: '',
    cacheReadPerMillion: '',
    cacheWritePerMillion: '',
    outputPerMillion: '',
    fxRate: '',
});

const EMPTY_TOTALS: AiUsageSummary['totals'] = {
    calls: 0,
    successCalls: 0,
    failedCalls: 0,
    users: 0,
    linkedOrders: 0,
    unlinkedCalls: 0,
    pricedCalls: 0,
    inputTokens: 0,
    cachedReadTokens: 0,
    cacheWriteTokens: 0,
    outputTokens: 0,
    totalCostUsd: null,
    totalCostAud: null,
    cacheHitCalls: 0,
    cacheHitRate: 0,
    costPerCallUsd: null,
    costPerCallAud: null,
    costPerLinkedOrderUsd: null,
    costPerLinkedOrderAud: null,
};

/**
 * AI Usage & Cost — the settings-tab view of ai_usage_logs.
 *
 * Read-only by design: the ledger is written by the parse route, and rates are managed
 * through /api/ai-usage/rates. Admin + Account Owner only (also enforced server-side by
 * read:ai_usage_dashboard / read:ai_usage_log).
 */
export default function AiUsageCostPanel() {
    const { currentUser } = useAuth();
    const [period, setPeriod] = useState<Period>('month');
    const [summary, setSummary] = useState<AiUsageSummary | null>(null);
    const [recent, setRecent] = useState<AiUsageCall[]>([]);
    const [recentTotal, setRecentTotal] = useState(0);
    const [rates, setRates] = useState<AiModelRate[]>([]);
    const [rateOpen, setRateOpen] = useState(false);
    const [rateForm, setRateForm] = useState(emptyRateForm);
    const [savingRate, setSavingRate] = useState(false);
    const [pricingExisting, setPricingExisting] = useState(false);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    const canView = currentUser?.userRole === 'Admin' || currentUser?.userRole === 'Account Owner';

    const load = useCallback(async () => {
        setLoading(true);
        setError(null);
        const range = periodRange(period);
        try {
            const [nextSummary, nextList, nextRates] = await Promise.all([
                aiUsageApi.summary(range),
                aiUsageApi.list({ ...range, page: 1, pageSize: 10 }),
                aiUsageApi.rates(),
            ]);
            setSummary(nextSummary);
            setRecent(nextList.data);
            setRecentTotal(nextList.total);
            setRates(nextRates);
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Could not load AI usage.');
        } finally {
            setLoading(false);
        }
    }, [period]);

    useEffect(() => {
        if (canView) load();
    }, [canView, load]);

    const submitRate = async () => {
        const model = rateForm.model.trim();
        const inputPerMillion = parseRateField(rateForm.inputPerMillion);
        const cacheReadPerMillion = parseRateField(rateForm.cacheReadPerMillion);
        const cacheWritePerMillion = parseRateField(rateForm.cacheWritePerMillion);
        const outputPerMillion = parseRateField(rateForm.outputPerMillion);
        const fxRate = rateForm.fxRate.trim() === '' ? null : parseRateField(rateForm.fxRate);

        if (!model || !rateForm.effectiveFrom) {
            toast.error('Model and effective date are required.');
            return;
        }
        if (
            inputPerMillion === null ||
            cacheReadPerMillion === null ||
            cacheWritePerMillion === null ||
            outputPerMillion === null
        ) {
            toast.error('Enter a non-negative rate for every token bucket.');
            return;
        }
        if (rateForm.fxRate.trim() !== '' && fxRate === null) {
            toast.error('FX rate must be zero or more.');
            return;
        }

        setSavingRate(true);
        try {
            await aiUsageApi.createRate({
                model,
                effectiveFrom: rateForm.effectiveFrom,
                inputPerMillion,
                cacheReadPerMillion,
                cacheWritePerMillion,
                outputPerMillion,
                fxRate,
            });
            toast.success('Rate added. Future calls will use it.');
            setRateOpen(false);
            setRateForm(emptyRateForm());
            await load();
        } catch (err) {
            toast.error(err instanceof Error ? err.message : 'Could not save the rate.');
        } finally {
            setSavingRate(false);
        }
    };

    const priceExistingCalls = async () => {
        setPricingExisting(true);
        try {
            const result = await aiUsageApi.backfillCosts(periodRange(period));
            toast.success(
                result.updated > 0
                    ? `${result.updated} call${result.updated === 1 ? '' : 's'} priced for this period.`
                    : 'No unpriced calls in this period matched a rate row.'
            );
            await load();
        } catch (err) {
            toast.error(err instanceof Error ? err.message : 'Could not price existing calls.');
        } finally {
            setPricingExisting(false);
        }
    };

    if (!currentUser) return null;
    if (!canView) {
        return (
            <AccessRestricted
                accessMessage="AI usage and cost are available to the Account Owner and Admin only."
                navigateTo="/settings"
                backMessage="Back to Settings"
            />
        );
    }

    const totals = summary?.totals ?? EMPTY_TOTALS;
    const orderRows = summary?.byOrderType ?? [];
    const modelRows = summary?.byModel ?? [];
    const hasCalls = totals.calls > 0;
    const hasPricedCalls = totals.pricedCalls > 0;
    const unpricedCalls = Math.max(0, totals.calls - totals.pricedCalls);

    return (
        <div className="space-y-4 md:space-y-6">
            <div className="flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
                <div>
                    <h2 className="flex items-center gap-2 text-xl font-semibold">
                        <Sparkles size={20} className="text-primary" />
                        AI Usage &amp; Cost
                    </h2>
                    <p className="mt-1 text-sm text-muted-foreground">
                        Gear-list parse calls, token usage and spend at the effective rate card.
                        Times are Australia/Sydney.
                    </p>
                </div>
                <div className="flex w-full items-center gap-2 md:w-auto">
                    <Select value={period} onValueChange={(value) => setPeriod(value as Period)}>
                        <SelectTrigger className="w-full md:w-[180px]">
                            <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                            {PERIODS.map((option) => (
                                <SelectItem key={option.value} value={option.value}>
                                    {option.label}
                                </SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                    <Button variant="outline" size="icon" onClick={load} disabled={loading} title="Refresh">
                        <RefreshCw size={16} className={loading ? 'animate-spin' : undefined} />
                    </Button>
                </div>
            </div>

            {error ? (
                <Card>
                    <CardContent className="flex items-start gap-3 pt-6 text-sm">
                        <AlertTriangle className="mt-0.5 h-5 w-5 text-destructive" />
                        <div>
                            <p className="font-medium">Could not load AI usage</p>
                            <p className="text-muted-foreground">{error}</p>
                        </div>
                    </CardContent>
                </Card>
            ) : loading && !summary ? (
                <Card>
                    <CardContent className="flex items-center justify-center gap-2 py-16 text-sm text-muted-foreground">
                        <Loader2 className="h-4 w-4 animate-spin" />
                        Loading AI usage…
                    </CardContent>
                </Card>
            ) : (
                <>
                    <div className="grid grid-cols-2 gap-3 lg:grid-cols-3 xl:grid-cols-6">
                        <MetricCard
                            title="Spend (USD)"
                            value={hasPricedCalls ? money(totals.totalCostUsd) : '—'}
                            icon={DollarSign}
                            iconClassName="bg-emerald-600"
                        />
                        <MetricCard
                            title="Spend (AUD)"
                            value={hasPricedCalls && totals.totalCostAud ? money(totals.totalCostAud) : '—'}
                            icon={DollarSign}
                            iconClassName="bg-teal-600"
                        />
                        <MetricCard
                            title="Calls"
                            value={num(totals.calls)}
                            icon={Sparkles}
                            iconClassName="bg-slate-900"
                        />
                        <MetricCard
                            title="Cost / call"
                            value={hasPricedCalls ? money(totals.costPerCallUsd) : '—'}
                            icon={ReceiptText}
                            iconClassName="bg-indigo-600"
                        />
                        <MetricCard
                            title="Cost / linked txn"
                            value={hasPricedCalls ? money(totals.costPerLinkedOrderUsd) : '—'}
                            icon={CheckCircle2}
                            iconClassName="bg-blue-600"
                        />
                        <MetricCard
                            title="Cache hit rate"
                            value={hasCalls ? `${(Number(totals.cacheHitRate ?? 0) * 100).toFixed(1)}%` : '—'}
                            icon={Database}
                            iconClassName="bg-violet-600"
                        />
                    </div>

                    <div className="space-y-1 text-xs text-muted-foreground">
                        <p>
                            {num(totals.successCalls)} successful · {num(totals.failedCalls)} failed ·{' '}
                            {num(totals.linkedOrders)} linked order{totals.linkedOrders === 1 ? '' : 's'} ·{' '}
                            {num(totals.unlinkedCalls)} unlinked call{totals.unlinkedCalls === 1 ? '' : 's'}
                        </p>
                        {hasCalls && !hasPricedCalls && (
                            <p className="text-amber-700">
                                No rate card has priced these calls yet. Tokens are recorded; add an effective
                                rate row to calculate spend.
                            </p>
                        )}
                        {hasPricedCalls && unpricedCalls > 0 && (
                            <p className="text-amber-700">
                                {num(unpricedCalls)} call{unpricedCalls === 1 ? ' is' : 's are'} still unpriced —
                                add a rate row or run the cost backfill.
                            </p>
                        )}
                    </div>

                    <Card>
                        <CardHeader>
                            <CardTitle className="text-base">Cost by transaction type</CardTitle>
                        </CardHeader>
                        <CardContent className="p-0">
                            {orderRows.length === 0 ? (
                                <p className="px-6 pb-6 text-sm text-muted-foreground">
                                    No AI calls in this period.
                                </p>
                            ) : (
                                <Table>
                                    <TableHeader>
                                        <TableRow>
                                            <TableHead>Transaction</TableHead>
                                            <TableHead className="text-right">Calls</TableHead>
                                            <TableHead className="text-right">Orders</TableHead>
                                            <TableHead className="text-right">Tokens</TableHead>
                                            <TableHead className="text-right">Spend (USD)</TableHead>
                                            <TableHead className="text-right">Cost / order</TableHead>
                                        </TableRow>
                                    </TableHeader>
                                    <TableBody>
                                        {orderRows.map((row) => (
                                            <TableRow key={row.orderType ?? 'unlinked'}>
                                                <TableCell className="font-medium">
                                                    {orderTypeLabel(row.orderType)}
                                                </TableCell>
                                                <TableCell className="text-right">{num(row.calls)}</TableCell>
                                                <TableCell className="text-right">{num(row.orders)}</TableCell>
                                                <TableCell className="text-right">{num(totalTokens(row))}</TableCell>
                                                <TableCell className="text-right">
                                                    {row.pricedCalls > 0 ? money(row.totalCostUsd) : '—'}
                                                </TableCell>
                                                <TableCell className="text-right">
                                                    {row.pricedCalls > 0 ? money(row.costPerOrderUsd) : '—'}
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            )}
                        </CardContent>
                    </Card>

                    <Card>
                        <CardHeader>
                            <CardTitle className="text-base">Cost by model</CardTitle>
                        </CardHeader>
                        <CardContent className="p-0">
                            {modelRows.length === 0 ? (
                                <p className="px-6 pb-6 text-sm text-muted-foreground">
                                    No model calls in this period.
                                </p>
                            ) : (
                                <Table>
                                    <TableHeader>
                                        <TableRow>
                                            <TableHead>Model</TableHead>
                                            <TableHead className="text-right">Calls</TableHead>
                                            <TableHead className="text-right">Input</TableHead>
                                            <TableHead className="text-right">Cache read</TableHead>
                                            <TableHead className="text-right">Cache write</TableHead>
                                            <TableHead className="text-right">Output</TableHead>
                                            <TableHead className="text-right">Spend (USD)</TableHead>
                                        </TableRow>
                                    </TableHeader>
                                    <TableBody>
                                        {modelRows.map((row) => (
                                            <TableRow key={`${row.provider}:${row.model ?? 'unknown'}`}>
                                                <TableCell className="font-medium">
                                                    {row.model || 'Unknown model'}
                                                </TableCell>
                                                <TableCell className="text-right">{num(row.calls)}</TableCell>
                                                <TableCell className="text-right">{num(row.inputTokens)}</TableCell>
                                                <TableCell className="text-right">{num(row.cachedReadTokens)}</TableCell>
                                                <TableCell className="text-right">{num(row.cacheWriteTokens)}</TableCell>
                                                <TableCell className="text-right">{num(row.outputTokens)}</TableCell>
                                                <TableCell className="text-right">
                                                    {row.pricedCalls > 0 ? money(row.totalCostUsd) : '—'}
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            )}
                        </CardContent>
                    </Card>

                    <Card>
                        <CardHeader className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                            <div>
                                <CardTitle className="text-base">Rate card</CardTitle>
                                <p className="mt-1 text-sm text-muted-foreground">
                                    Cost = tokens × these per-million rates. A new row applies from its
                                    effective date.
                                </p>
                            </div>
                            <div className="flex flex-wrap items-center gap-2">
                                {unpricedCalls > 0 && rates.length > 0 && (
                                    <Button
                                        variant="outline"
                                        onClick={priceExistingCalls}
                                        disabled={pricingExisting}
                                    >
                                        <RefreshCw size={16} className={pricingExisting ? 'animate-spin' : undefined} />
                                        {pricingExisting ? 'Pricing…' : 'Price existing calls'}
                                    </Button>
                                )}
                                <Button onClick={() => setRateOpen(true)}>
                                    <Plus size={16} />
                                    Add rate
                                </Button>
                            </div>
                        </CardHeader>
                        <CardContent className="p-0">
                            {rates.length === 0 ? (
                                <p className="px-6 pb-6 text-sm text-muted-foreground">
                                    No rate rows yet. Add one so calls can be costed.
                                </p>
                            ) : (
                                <Table>
                                    <TableHeader>
                                        <TableRow>
                                            <TableHead>Model</TableHead>
                                            <TableHead>Effective from</TableHead>
                                            <TableHead className="text-right">Input $/M</TableHead>
                                            <TableHead className="text-right">Cache read $/M</TableHead>
                                            <TableHead className="text-right">Cache write $/M</TableHead>
                                            <TableHead className="text-right">Output $/M</TableHead>
                                            <TableHead className="text-right">FX</TableHead>
                                        </TableRow>
                                    </TableHeader>
                                    <TableBody>
                                        {rates.map((rate) => (
                                            <TableRow key={rate.id}>
                                                <TableCell>
                                                    <p className="font-medium">{rate.model}</p>
                                                    <p className="text-xs text-muted-foreground">
                                                        {rate.provider}
                                                    </p>
                                                </TableCell>
                                                <TableCell className="whitespace-nowrap">
                                                    {rate.effectiveFrom}
                                                </TableCell>
                                                <TableCell className="text-right">
                                                    {rateMoney(rate.inputPerMillion)}
                                                </TableCell>
                                                <TableCell className="text-right">
                                                    {rateMoney(rate.cacheReadPerMillion)}
                                                </TableCell>
                                                <TableCell className="text-right">
                                                    {rateMoney(rate.cacheWritePerMillion)}
                                                </TableCell>
                                                <TableCell className="text-right">
                                                    {rateMoney(rate.outputPerMillion)}
                                                </TableCell>
                                                <TableCell className="text-right">
                                                    {rate.fxRate === null ? '—' : rateMoney(rate.fxRate)}
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            )}
                        </CardContent>
                    </Card>

                    <Dialog
                        open={rateOpen}
                        onOpenChange={(open) => {
                            setRateOpen(open);
                            if (!open) setRateForm(emptyRateForm());
                        }}
                    >
                        <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-[620px]">
                            <DialogHeader>
                                <DialogTitle>Add a rate row</DialogTitle>
                                <DialogDescription>
                                    Rates are per million tokens. The row applies from its effective date
                                    in Australia/Sydney. Add a new row when a price changes; existing rows
                                    are not edited.
                                </DialogDescription>
                            </DialogHeader>
                            <div className="grid gap-4 sm:grid-cols-2">
                                <div className="space-y-2 sm:col-span-2">
                                    <Label htmlFor="ai-rate-model">Model</Label>
                                    <Input
                                        id="ai-rate-model"
                                        value={rateForm.model}
                                        onChange={(event) => setRateForm((prev) => ({
                                            ...prev, model: event.target.value,
                                        }))}
                                        placeholder="claude-sonnet-5"
                                    />
                                </div>
                                <div className="space-y-2">
                                    <Label htmlFor="ai-rate-effective">Effective from</Label>
                                    <Input
                                        id="ai-rate-effective"
                                        type="date"
                                        value={rateForm.effectiveFrom}
                                        onChange={(event) => setRateForm((prev) => ({
                                            ...prev, effectiveFrom: event.target.value,
                                        }))}
                                    />
                                </div>
                                <div className="space-y-2">
                                    <Label htmlFor="ai-rate-fx">FX rate (USD → AUD)</Label>
                                    <Input
                                        id="ai-rate-fx"
                                        type="number"
                                        min="0"
                                        step="0.000001"
                                        value={rateForm.fxRate}
                                        onChange={(event) => setRateForm((prev) => ({
                                            ...prev, fxRate: event.target.value,
                                        }))}
                                        placeholder="optional"
                                    />
                                </div>
                                <div className="space-y-2">
                                    <Label htmlFor="ai-rate-input">Input $ / 1M</Label>
                                    <Input
                                        id="ai-rate-input"
                                        type="number"
                                        min="0"
                                        step="0.000001"
                                        value={rateForm.inputPerMillion}
                                        onChange={(event) => setRateForm((prev) => ({
                                            ...prev, inputPerMillion: event.target.value,
                                        }))}
                                    />
                                </div>
                                <div className="space-y-2">
                                    <Label htmlFor="ai-rate-cache-read">Cache read $ / 1M</Label>
                                    <Input
                                        id="ai-rate-cache-read"
                                        type="number"
                                        min="0"
                                        step="0.000001"
                                        value={rateForm.cacheReadPerMillion}
                                        onChange={(event) => setRateForm((prev) => ({
                                            ...prev, cacheReadPerMillion: event.target.value,
                                        }))}
                                    />
                                </div>
                                <div className="space-y-2">
                                    <Label htmlFor="ai-rate-cache-write">Cache write $ / 1M</Label>
                                    <Input
                                        id="ai-rate-cache-write"
                                        type="number"
                                        min="0"
                                        step="0.000001"
                                        value={rateForm.cacheWritePerMillion}
                                        onChange={(event) => setRateForm((prev) => ({
                                            ...prev, cacheWritePerMillion: event.target.value,
                                        }))}
                                    />
                                </div>
                                <div className="space-y-2">
                                    <Label htmlFor="ai-rate-output">Output $ / 1M</Label>
                                    <Input
                                        id="ai-rate-output"
                                        type="number"
                                        min="0"
                                        step="0.000001"
                                        value={rateForm.outputPerMillion}
                                        onChange={(event) => setRateForm((prev) => ({
                                            ...prev, outputPerMillion: event.target.value,
                                        }))}
                                    />
                                </div>
                            </div>
                            <DialogFooter>
                                <Button variant="outline" onClick={() => setRateOpen(false)}>
                                    Cancel
                                </Button>
                                <Button onClick={submitRate} disabled={savingRate}>
                                    {savingRate ? 'Saving…' : 'Save rate'}
                                </Button>
                            </DialogFooter>
                        </DialogContent>
                    </Dialog>

                    <Card>
                        <CardHeader className="flex items-center justify-between">
                            <CardTitle className="text-base">Recent calls</CardTitle>
                            <span className="text-xs text-muted-foreground">
                                Latest {Math.min(recent.length, 10)} of {num(recentTotal)}
                            </span>
                        </CardHeader>
                        <CardContent className="p-0">
                            {recent.length === 0 ? (
                                <p className="px-6 pb-6 text-sm text-muted-foreground">
                                    No AI calls recorded in this period.
                                </p>
                            ) : (
                                <Table>
                                    <TableHeader>
                                        <TableRow>
                                            <TableHead>When</TableHead>
                                            <TableHead>User</TableHead>
                                            <TableHead>Order / input</TableHead>
                                            <TableHead>Model</TableHead>
                                            <TableHead>Status</TableHead>
                                            <TableHead className="text-right">Tokens</TableHead>
                                            <TableHead className="text-right">Cost (USD)</TableHead>
                                        </TableRow>
                                    </TableHeader>
                                    <TableBody>
                                        {recent.map((call) => (
                                            <TableRow key={call.id}>
                                                <TableCell className="whitespace-nowrap text-xs text-muted-foreground">
                                                    {sydneyTimestamp(call.createdAt)}
                                                </TableCell>
                                                <TableCell>
                                                    <p className="text-sm font-medium">{call.user?.name || 'Unknown'}</p>
                                                    <p className="text-xs text-muted-foreground">
                                                        {call.user?.userRole || '—'}
                                                    </p>
                                                </TableCell>
                                                <TableCell>
                                                    <p className="text-sm">
                                                        {call.relatedEntityLabel || (call.inputKind === 'upload'
                                                            ? 'Gear sheet upload'
                                                            : 'Typed list')}
                                                    </p>
                                                    <p className="text-xs text-muted-foreground">
                                                        {call.relatedEntityLabel ? 'Stock order' : 'Not linked'}
                                                    </p>
                                                </TableCell>
                                                <TableCell className="whitespace-nowrap text-sm">
                                                    {call.model || '—'}
                                                </TableCell>
                                                <TableCell>
                                                    <span className={cn(
                                                        'inline-flex rounded-full border px-2 py-0.5 text-xs font-medium',
                                                        statusPillClass(call.status)
                                                    )}>
                                                        {call.status.replace(/_/g, ' ')}
                                                    </span>
                                                </TableCell>
                                                <TableCell className="text-right">{num(totalTokens(call.tokens))}</TableCell>
                                                <TableCell className="text-right">
                                                    {call.totalCostUsd === null ? '—' : money(call.totalCostUsd)}
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            )}
                        </CardContent>
                    </Card>
                </>
            )}
        </div>
    );
}
